<script setup>

import {computed, getCurrentInstance, ref, watch} from "vue";
import {useRoute} from "vue-router";
import { useI18n } from "vue-i18n";
import {FRONTEND_BASEURL} from "@/config/config.js";
import { useUserStore } from "@/lib/es-bill-shared-js/auth/userStore";

// Atomic Design Imports
import DropdownMenu from '@/components/molecule/DropdownMenu.vue';
import MobileMenu from '@/components/molecule/MobileMenu.vue';

const route = useRoute();
const { t, locale } = useI18n();
const isSidebarVisible = ref(false);
const userStore = useUserStore();

let routeIsViewDsoContracts = computed(() => route.name === "dsoContracts")
let routeIsViewReplaceDsoCompanies = computed(() => route.name === "replaceDsoCompanies")
let routeIsViewDsoCompanies = computed(() => route.name === "dsoCompanies")
let routeIsViewDsoCompanyForm = computed(() => route.name === "dsoCompanyForm")
let routeIsViewEdaConnectionForm = computed(() => route.name === "edaConnectionForm")
let routeIsViewEdaConnections = computed(() => route.name === "edaConnections")
let routeIsViewMeteringPoints = computed(() => route.name === "meteringPoints")
let routeIsViewEnergyDataCleanup = computed(() => route.name === "energyDataCleanup")
let routeIsViewEnergyDataHistory = computed(() => route.name === "energyDataHistory")
let routeIsViewSumsReport = computed(() => route.name === "sumsReport")
let routeIsViewMeteringPointsCm = computed(() => route.name === "meteringPointsCm")
let routeIsViewMeteringPointsAnalysis = computed(() => route.name === "meteringPointsAnalysis")
let routeIsViewMeteringPointsAnalysis1 = computed(() => route.name === "meteringPointsAnalysis1")
let routeIsViewEnergyDump = computed(() => route.name === "ViewExportEnergyDump")
let routeIsViewStatistics = computed(() => route.name === "statistics")
let routeIsViewCommunities = computed(() => route.name === "communities")
let routeIsViewCommunityProviders = computed(() => route.name === "communityProviders")
let routeIsViewCommunityProviderForm = computed(() => route.name === "communityProviderForm")
let routeIsViewCommunityCompanies = computed(() => route.name === "communityCompanies")
let routeIsViewCommunityCompanyForm = computed(() => route.name === "communityCompanyForm")
let routeIsViewCommunityDetailForm = computed(() => route.name === "communityDetailForm")
let routeIsViewCommunityForm = computed(() => route.name === "communityForm")
let routeIsViewMessenger = computed(() => route.name === "messenger")
let routeIsViewCreatePartner = computed(() => route.name === "createPartner")
let routeIsViewDashboard = computed(() => route.name === "dashboard")

let routeIsViewExportOdoo = computed(() => route.name === "ViewExportOdoo")

// notification contacts route detection
let routeIsViewNotificationContacts = computed(() => route.name === "notificationContacts");

let routeIsViewEnergyDataRequests = computed(() => route.name === "energyDataRequests")

// Add computed for Bill Runs
let routeIsViewBillrunsDashboard = computed(() => route.name === "billrunsDashboard")
let routeIsViewBillrunsDetails = computed(() => route.name === "billrunsDetails")
let routeIsViewBillrunsAnalysis = computed(() => route.name === "billrunsAnalysis")

// Add computed for Maintenance menu items
let routeIsViewMaintainers = computed(() => route.name === "ViewAllMaintainers")
let routeIsViewCamtPreview = computed(() => route.name === "ViewCamtPreview")
let routeIsViewBadMeteringPoints = computed(() => route.name === "ViewCommunityAnalysisBadMeteringPoints")
let routeIsViewPodlistViewer = computed(() => route.name === "ViewCommunityPodlistViewer")

const onboardingMenuItems = [
  {text: 'communityProviders.title', path: '/communityProviders', active: routeIsViewCommunityProviders},
  {text: 'communityCompanies.title', path: '/communityCompanies', active: routeIsViewCommunityCompanies},
  {text: 'publicCommunityInformation', path: '/communities', active: routeIsViewCommunities},
  {text: 'notificationContacts', path: '/notificationContacts', active: routeIsViewNotificationContacts},
  {text: 'dsoContracts', path: '/dsoContracts', active: routeIsViewDsoContracts},
  {text: 'xp-messenger.partner.create', path: '/messenger/create-partner', active: routeIsViewCreatePartner},
  {text: 'edaConnections', path: '/edaConnections', active: routeIsViewEdaConnections},
];

const maintenanceMenuItems = [
  {text: 'meteringPoints', path: '/meteringPoints', active: routeIsViewMeteringPoints},
  {text: 'meteringPointsCm', path: '/meteringPointsCm', active: routeIsViewMeteringPointsCm},
  {text: 'energyDataRequests', path: '/energyDataRequests', active: routeIsViewEnergyDataRequests},
  {text: 'slr-evaluations', path: '/communities/analysis/badMeteringPoints', active: routeIsViewBadMeteringPoints},
  {text: 'ec-podlist-viewer', path: '/communities/podlistViewer', active: routeIsViewPodlistViewer},
  {text: 'energyDataCleanup.title', path: '/energyDataCleanup', active: routeIsViewEnergyDataCleanup},
  {text: 'energyDataHistory.title', path: '/energyDataHistory', active: routeIsViewEnergyDataHistory},
  {text: 'sumsReport.title', path: '/sumsReport', active: routeIsViewSumsReport},
  // {text: 'analysisMess', path: '/meteringPoints/analysis1', active: routeIsViewMeteringPointsAnalysis1},
  {text: 'adminui.menu.energyDump', path: '/exports/energyDump', active: routeIsViewEnergyDump},
];

const soStromMenuItems = [
  {text: 'statistics', path: '/statistics', active: routeIsViewStatistics},
  {text: 'analysisMps', path: '/meteringPoints/analysis', active: routeIsViewMeteringPointsAnalysis},
  {text: 'export-odoo', path: '/exports/odoo', active: routeIsViewExportOdoo},
  {text: 'maintainers', path: '/maintainers', active: routeIsViewMaintainers},
  {text: 'camt-preview', path: '/misc/camt-preview', active: routeIsViewCamtPreview},
];

// Add Bill Runs menu items constant
const billRunsMenuItems = [
  { text: 'billrunsDashboard.dashboard', path: '/billRuns/dashboard', active: routeIsViewBillrunsDashboard },
  { text: 'billrunsDetails.title', path: '/billRuns/details', active: routeIsViewBillrunsDetails },
  { text: 'analysis', path: '/billRuns/analysis', active: routeIsViewBillrunsAnalysis },
]

const adminMenuItems = [
  {text: 'dsoCompanies', path: '/dsoCompanies', active: routeIsViewDsoCompanies},
  {text: 'kep-dashboard', path: '/messenger/dashboard', active: routeIsViewDashboard},
];

const keycloak = getCurrentInstance().appContext.app.$keycloak;

// List of available locales
const locales = [
  {text: 'Deutsch', value: 'de'},
  {text: 'English', value: 'en'},
  {text: 'Italiano', value: 'it'}
];

// Computed properties for user data
const userInitials = computed(() => {
  return keycloak.tokenParsed?.given_name?.substring(0, 1) +
         keycloak.tokenParsed?.family_name?.substring(0, 1);
});

// Computed property for onboarding menu active state
const routeIsViewOnboarding = computed(() => routeIsViewDsoContracts.value || routeIsViewCommunityProviders.value || routeIsViewCommunityProviderForm.value || routeIsViewCommunityCompanies.value || routeIsViewCommunityCompanyForm.value || routeIsViewCommunityDetailForm.value || routeIsViewCommunities.value || routeIsViewEdaConnectionForm.value || routeIsViewEdaConnections.value || routeIsViewCreatePartner.value);

// Unified menu groups configuration for both desktop and mobile
const menuGroups = computed(() => [
  {
    title: 'adminui.menu.onboarding',
    items: onboardingMenuItems,
    isActive: routeIsViewOnboarding.value
  },
  {
    title: 'adminui.menu.maintenance',
    items: maintenanceMenuItems,
    isActive: routeIsViewMeteringPoints.value
      || routeIsViewBadMeteringPoints.value
      || routeIsViewPodlistViewer.value
      || routeIsViewEnergyDataRequests.value
      || routeIsViewMeteringPointsAnalysis1.value
  },
  {
    title: 'So-Strom',
    items: soStromMenuItems,
    isActive: routeIsViewMeteringPointsAnalysis.value
      || routeIsViewStatistics.value
      || routeIsViewExportOdoo.value
      || routeIsViewMaintainers.value
      || routeIsViewCamtPreview.value
      || routeIsViewEnergyDump.value
  },
  {
    title: 'adminui.menu.billRuns',
    items: billRunsMenuItems,
    isActive: routeIsViewBillrunsDashboard.value
      || routeIsViewBillrunsDetails.value
      || routeIsViewBillrunsAnalysis.value
  },
  {
    title: 'admin',
    items: adminMenuItems,
    isActive: routeIsViewDsoCompanies.value
      || routeIsViewDashboard.value
  }
]);

function redirectProfileEdit() {
  keycloak.accountManagement({redirectUri: FRONTEND_BASEURL});
}

function handleLogout() {
  // userStore.logout();
  keycloak.logout();
}
</script>

<template>
  <v-app-bar scroll-threshold="50" :scroll-behavior="$vuetify.display.smAndDown && !isSidebarVisible ? 'hide' : ''" height="80" v-bind="$attrs">
    <template v-slot:prepend>
      <div class="justify-start headerTitle menuBand">
        <router-link to="/"
          class="routerLinkNoDecoration">
          <img height="25px" width="120px" src="@/assets/sostrom/logo.svg" alt="so-strom-Logo"/>
        </router-link>
      </div>
      <div class="justify-start headerTitle menuBand hidden-md-and-down">
        <DropdownMenu
          v-for="group in menuGroups"
          :key="group.title"
          :title="$t(group.title)"
          :menu-items="group.items"
          :is-active="group.isActive"
        />
      </div>
    </template>

    <template v-slot:append>
      <div class="headerIcons hidden-md-and-down">
        <v-select
            v-model="locale"
            :items="locales"
            item-title="text"
            item-value="value"
            variant="underlined"
            dense
        ></v-select>

        <v-btn
            icon
        >
          <v-avatar color="primary" class="avatarIcon" @click="redirectProfileEdit()">
          <span class="avatarIconText">
              {{
                keycloak.tokenParsed?.given_name?.substring(0, 1) +
                keycloak.tokenParsed?.family_name?.substring(0, 1)
              }}</span>
          </v-avatar>
        </v-btn>

        <v-btn size="normal" icon="mdi-logout" color="#878787" @click="handleLogout"></v-btn>
      </div>

      <div class="hidden-lg-and-up">
        <v-app-bar-nav-icon color="black" @click="isSidebarVisible = !isSidebarVisible"></v-app-bar-nav-icon>
      </div>

    </template>
  </v-app-bar>

  <MobileMenu
    v-model:is-visible="isSidebarVisible"
    :menu-groups="menuGroups"
    :user-initials="userInitials"
    :full-name="userStore.fullName"
    :locales="locales"
    @profile-edit="redirectProfileEdit"
    @logout="handleLogout"
  />
</template>


<script>
export default {
  name: 'HeaderBar'
}
</script>


<style scoped>
.routerLinkNoDecoration {
  text-decoration: none;
  color: inherit;
}

.v-select {
  min-width: 100px;
}



.menuBand {
  padding: 12px 12px 12px 32px;

  display: flex;
  align-items: center;
  gap: 40px;
}

.headerIcons {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-right: 32px;
}

.avatarIcon {
  height: 40px;
  width: 40px;
}

.avatarIconText {
  color: white;
}


</style>
